/**
 * Button is used for anything that involves user interaction in the Inquiry flow.
 * Although it typically uses the <button> tag, you can swap that out
 * by using "as" polymorphic prop, e.g. <Button as="a">.
 */
import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import {
  BORDER_RADIUSES,
  FONT_SIZES,
  FONT_WEIGHTS,
  LINE_HEIGHTS,
  SPACINGS,
  TRANSITIONS,
} from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import ButtonBase from 'inquiry/components/ButtonBase';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ThemeSerialized from 'lib/interfaces/ThemeSerialized';
import { ButtonComponentStyles } from 'lib/interfaces/ui-components';

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
}

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  buttonType?: ButtonType;
  disabled?: boolean;
  href?: string;
  isPending?: boolean;
  type?: 'button' | 'reset' | 'submit';
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  styles?: ButtonComponentStyles;
}

function innerButtonTypeStyles(type: ButtonType, theme: Partial<ThemeSerialized>) {
  const colorButtonPrimary = theme.colorButtonPrimary || theme.colorPrimary;
  const colorButtonPrimaryLightened = Color(colorButtonPrimary).mix(Color('white'), 0.2).hex();
  const colorButtonPrimaryLightened2 = Color(colorButtonPrimary).mix(Color('white'), 0.4).hex();

  const colorButtonSecondary = theme.colorButtonSecondary || theme.colorPrimary;
  const colorButtonSecondaryLightened = Color(colorButtonSecondary).mix(Color('white'), 0.2).hex();
  const colorButtonSecondaryLightened2 = Color(colorButtonSecondary).mix(Color('white'), 0.4).hex();

  const colorButtonSecondaryFill = theme.colorButtonSecondaryFill;
  const colorButtonSecondaryFillLightened =
    colorButtonSecondaryFill != null
      ? Color(colorButtonSecondaryFill).mix(Color('white'), 0.2).hex()
      : undefined;
  const colorButtonSecondaryFillLightened2 =
    colorButtonSecondaryFill != null
      ? Color(colorButtonSecondaryFill).mix(Color('white'), 0.4).hex()
      : undefined;

  switch (type) {
    // NB: default is primary
    default:
    case ButtonType.Primary: {
      /* TODO [strict-migration] The following is possibly undefined, please fix! */
      const buttonShadowStrength = theme.buttonShadowStrength as any;
      return css`
        background-color: ${colorButtonPrimary};
        background-image: ${theme.buttonBackgroundImage};
        border: ${theme.borderWidth} solid ${colorButtonPrimary};
        color: ${theme.colorFontButtonPrimary};

        svg:not(.pds-icon) path {
          stroke: ${theme.colorFontButtonPrimary};
        }

        ${buttonShadowStrength > 0 &&
        css`
          box-shadow: 0 3px 10px 0px ${PersonaColor.CoreSlate500};
        `};

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
          background-color: ${colorButtonPrimaryLightened};
          border-color: ${colorButtonPrimaryLightened};
          ${buttonShadowStrength > 0 &&
          css`
            box-shadow: 0 6px 15px 0px ${PersonaColor.CoreSlate500};
          `};
        }

        &:active:not(:disabled) {
          background-color: ${colorButtonPrimaryLightened2};
          border-color: ${colorButtonPrimaryLightened2};
          box-shadow: none;
        }

        &:disabled {
          background-color: ${theme.colorButtonPrimaryFillDisabled};
          border-color: ${theme.colorButtonPrimaryFillDisabled};
          background-image: none;
          box-shadow: none;
        }

        &:focus-visible {
          outline-color: ${colorButtonPrimaryLightened};
          outline-width: 2px;
          outline-offset: 2px;
          outline-style: solid;
        }
      `;
    }
    case ButtonType.Secondary:
      return css`
        border: ${theme.borderWidth} solid ${colorButtonSecondary};
        background-color: ${colorButtonSecondaryFill ??
        theme.colorModalBackground ??
        PersonaColor.PrimaryWhite};
        color: ${theme.colorFontButtonSecondary ?? colorButtonSecondary};

        svg:not(.pds-icon) path {
          stroke: ${theme.colorFontButtonSecondary ?? colorButtonSecondary};
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
          border: ${theme.borderWidth} solid ${colorButtonSecondaryLightened};
          background-color: ${colorButtonSecondaryFillLightened ?? PersonaColor.CoreSlate50};
        }

        &:active:not(:disabled) {
          border: ${theme.borderWidth} solid ${colorButtonSecondaryLightened2};
          background-color: ${colorButtonSecondaryFillLightened2 ?? PersonaColor.CoreSlate200};
        }

        &:disabled {
          background-color: ${colorButtonSecondaryFill != null
            ? theme.colorButtonSecondaryFillDisabled
            : PersonaColor.PrimaryWhite};
          border-color: ${theme.colorButtonSecondaryFillDisabled};
          color: ${PersonaColor.CoreSlate500};

          svg:not(.pds-icon) path {
            stroke: ${PersonaColor.CoreSlate500};
          }
        }

        &:focus-visible {
          outline-color: ${colorButtonSecondaryLightened};
          outline-width: 2px;
          outline-offset: 2px;
          outline-style: solid;
        }
      `;
  }
}

const ButtonStyles = css<ButtonProps>`
  ${(props) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return innerButtonTypeStyles(props.buttonType, props.theme);
  }}

  border-radius: ${(props) => props.theme.borderRadius ?? BORDER_RADIUSES.base};
  box-sizing: border-box;
  height: 48px;
  text-transform: ${(props) => props.theme.buttonTextTransform};
  padding: 0 ${SPACINGS.base * 2}px;
  font-size: ${FONT_SIZES.input};
  line-height: ${LINE_HEIGHTS.input};

  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Text styling */
  font-weight: ${(props) => props.theme.buttonFontWeight ?? FONT_WEIGHTS.medium};
  text-decoration: none;
  white-space: nowrap;

  transition: ${TRANSITIONS.fast};

  /* Always show a transparent outline so animation isn't janky and only fades in */
  outline-color: transparent;
  outline-width: 2px;
  outline-offset: 2px;
  outline-style: solid;

  &:disabled {
    cursor: not-allowed;
  }

  /* Use :not(:disabled) instead of :enabled to support anchors. Anchors don't support :enabled. */
  &:not(:active):not(:disabled) {
    &:hover,
    &:focus {
      transform: translateY(-2px);
    }
  }
`;

interface WithPendingProps {
  isPending: boolean;
}

const StyledLoadingIndicator = styled(InlineLoadingIndicator)<WithPendingProps>`
  position: absolute;
  transition: opacity 0.2s;
  ${(p) =>
    !p.isPending &&
    css`
      opacity: 0;
    `};
`;

const ButtonChildren = styled('div').attrs(({ isPending }: WithPendingProps) => ({
  ['aria-hidden']: isPending,
}))<WithPendingProps>`
  width: 100%;
  display: flex;
  flex-wrap: unset;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  ${(p) =>
    p.isPending &&
    css`
      opacity: 0;
    `};
`;

const PrefixIconContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: ${SPACINGS.base}px;
`;

const SuffixIconContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-left: ${SPACINGS.base}px;
`;

// We wrap the button so we can apply margins when using position: fixed
const StyledButton = styled('button')<ButtonProps>`
  ${ButtonStyles};
`;

const StyledLink = styled('a')<ButtonProps>`
  ${ButtonStyles};
`;

const Button = (props: ButtonProps) => {
  const {
    children,
    href,
    prefixIcon,
    suffixIcon,
    // When type is omitted and inside a <form/>, <button/>s submit the form.
    // This is really confusing and has caused plenty of bugs, so we require
    // this behavior to be explicitly requested by passing type = 'submit.'
    type = 'button',
    styles,
    ...otherProps
  } = props;

  if (styles != null) {
    return (
      <ButtonBase
        {...{
          href,
          prefixIcon,
          suffixIcon,
          type,
          styles,
          ...otherProps,
        }}
      >
        {prefixIcon != null && <PrefixIconContainer>{prefixIcon}</PrefixIconContainer>}
        {children}
        {suffixIcon != null && <SuffixIconContainer>{suffixIcon}</SuffixIconContainer>}
      </ButtonBase>
    );
  }

  return href ? (
    <StyledLink
      data-test={'button__' + props.buttonType}
      type={type}
      href={href}
      target='_blank'
      rel='noopener'
      buttonType={props.buttonType}
    >
      {props.isPending && <StyledLoadingIndicator isPending={props.isPending} />}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <ButtonChildren isPending={props.isPending}>
        {prefixIcon != null && <PrefixIconContainer>{prefixIcon}</PrefixIconContainer>}
        {children}
        {suffixIcon != null && <SuffixIconContainer>{suffixIcon}</SuffixIconContainer>}
      </ButtonChildren>
    </StyledLink>
  ) : (
    <StyledButton data-test={'button__' + props.buttonType} type={type} {...otherProps}>
      {props.isPending && <StyledLoadingIndicator isPending={props.isPending} />}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <ButtonChildren isPending={props.isPending}>
        {prefixIcon != null && <PrefixIconContainer>{prefixIcon}</PrefixIconContainer>}
        {children}
        {suffixIcon != null && <SuffixIconContainer>{suffixIcon}</SuffixIconContainer>}
      </ButtonChildren>
    </StyledButton>
  );
};

export default Button;
